import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";

import "./change-sale-lead-modal.scss";

/**
 * "Change Sale Lead" picker (#5). Lists the store's sales admins
 * (GET-by-type store_admin) and hands the chosen user id to onConfirm, which
 * reassigns the whole deal's lead (owner + 100% commission) via
 * PUT /requests/:id/sale-lead. Shared by the Kanban board and the
 * requests/orders list. onConfirm should throw on failure so the modal can show
 * an error and stay open.
 */
const ChangeSaleLeadModal = ({ onClose, onConfirm }) => {
  const [admins, setAdmins] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.post(`/api/users/by-type/store_admin`, { query: "" });
        if (!alive) return;
        setAdmins(
          (data.items || []).map((u) => ({
            value: u.id,
            label: `${u.first_name || ""} ${u.last_name || ""}`.trim() || `#${u.id}`,
          })),
        );
      } catch {
        if (alive) setError("Couldn’t load sales admins.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const confirm = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await onConfirm(selected.value);
    } catch {
      setError("Couldn’t change the sale lead. Please try again.");
      setSaving(false);
    }
  };

  return createPortal(
    <div className="cslm__overlay" onMouseDown={onClose}>
      <div className="cslm" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cslm__header">
          <h3>Change Sale Lead</h3>
          <button type="button" className="cslm__close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="cslm__body">
          <p className="cslm__hint">
            The selected admin becomes the sales lead for this whole deal (every order &amp; PX-sell in the
            group) and is set to 100% commission, replacing any existing split.
          </p>
          <select
            className="cslm__select"
            value={selected?.value ?? ""}
            disabled={loading || saving}
            onChange={(e) => {
              const v = e.target.value;
              setSelected(v ? admins.find((a) => String(a.value) === v) || null : null);
            }}
          >
            <option value="">{loading ? "Loading sales admins…" : "Select a sales admin…"}</option>
            {admins.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          {error && <div className="cslm__error">{error}</div>}
        </div>
        <div className="cslm__footer">
          <button type="button" className="cslm__btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="cslm__btn cslm__btn--primary"
            onClick={confirm}
            disabled={!selected || saving}
          >
            {saving ? "Saving…" : "Change Lead"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ChangeSaleLeadModal;
